import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@/component/Dialog';
import { DataProvider, Table } from '@/component/Table';
import StyledTable from '@/component/Table/flat/components/StyledTable';
import { Settings } from '@/component/Table/Settings';
import type { ExtendedColumnDef } from '@/component/Table/types';
import Translate from '@/component/Translate';
import { usePaperBackgroundColor } from '@/theme/hooks';
import { ONE_HOUR } from '@/util/time';
import { Button } from '@mui/material';
import { useState } from 'react';
import { useBusinessEntityQuery } from '../businessEntity/queries';
import { useTexts } from '../lang';
import criteriaBuilder from '../query/criteriaBuilder';
import { useAccountSelector } from './AccountProvider';

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
}

export const columnDefs: ExtendedColumnDef<AccountDto>[] = [
  {
    accessorKey: 'displayName',
    headerText: (texts) => texts.admin.company.model.name,
  },
];

const AccountDialog = ({ open, onClose }: AccountDialogProps) => {
  const { app, component } = useTexts();
  const { account, setAccount } = useAccountSelector();
  const [selectedAccount, setSelectedAccount] =
    useState<BusinessEntityBase | undefined>(account);
  const criteria = criteriaBuilder().type('region').type('account').get();
  const { data = [], isLoading } = useBusinessEntityQuery<BusinessEntityBase[]>(
    criteria,
    {},
    { enabled: open, queryKey: ['accountSelector'], staleTime: ONE_HOUR }
  );
  const backgroundColor = usePaperBackgroundColor(24);

  const handleSetAccount = () => {
    setAccount(selectedAccount);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle title={app.account.dialog.header} />
      <DialogContent sx={{ height: 700 }}>
        <Settings
          showFilterAction={false}
          showActiveFilters={false}
          showColumnFilterOnLoad={true}
        >
          <DataProvider
            columnDefs={columnDefs}
            initialData={data}
            isLoading={isLoading}
            initialState={{ sorting: [{ id: 'displayName', desc: false }] }}
            onRowClick={(row) => setSelectedAccount(row.original)}
            isRowHighlighted={(row) => row.original.id === selectedAccount?.id}
            enableSorting
            enablePagination
            enableFilters
          >
            <StyledTable backgroundColor={backgroundColor}>
              <Table />
            </StyledTable>
          </DataProvider>
        </Settings>
      </DialogContent>
      <DialogActions
        right={
          <>
            <Button type="button" variant="outlined" onClick={onClose}>
              <Translate text={component.button.common.cancel} />
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSetAccount}
            >
              <Translate text={component.button.common.ok} />
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

export default AccountDialog;
